/**
 * Invite Member Global Tool (owner directive 2026-08-13)
 *
 * "Users should be able to invite members right in the chat interface" —
 * this tool is the chat entry point for the Team & Roles invite flow. When a
 * message is a direct invite command ("invite jane@acme.com", "add a new
 * member", "invita a maría"), the step opens the SAME InviteModal the Team &
 * Roles settings page uses (full parity: name, email, member type, granular
 * role selection with the permission panel, one-click "invite another"),
 * prefilled with any email found in the trigger text.
 *
 * The modal mount + `chat:open-invite-member` listener live in
 * GlobalChatSystem; this step only dispatches the event and emits a
 * structured payload (DATA ONLY — the post-tool renderer composes the
 * user-visible prose, per the 2026-05-10 structured-events directive).
 *
 * Permission note: the invite itself is enforced server-side by the
 * rbacCreateInvitation callable (`team.invite`). Opening the form is
 * deliberately not client-gated — a user without permission gets the
 * server's honest error in the modal, never a silent no-op.
 */

import { isAgenticWorkflowsAuthorized } from '../../agenticWorkflows';
import { makeRegexTriggerWithExtractor } from '../triggerHelpers';

import type {
  GlobalToolDefinition,
  GlobalToolId,
  StepContext,
  StepResult,
  ToolTheme,
} from '../types';

// ============================================================================
// Constants
// ============================================================================

export const INVITE_MEMBER_TOOL_ID: GlobalToolId = 'invite-member';

/** Event the chat surface listens for; detail: { email?: string }. */
export const OPEN_INVITE_MEMBER_EVENT = 'chat:open-invite-member';

// Same warm platform palette as the connect card (2026-08-12 consistency
// directive: every chat element rides the platform scheme).
const INVITE_MEMBER_THEME: ToolTheme = {
  accent: '#1C1917',
  accentBg: '#FFFFFF',
  border: '#E5E0D5',
  subtitle: '#6B6459',
  muted: 'var(--brand-text-muted)',
};

// ============================================================================
// Trigger
// ============================================================================

const EMAIL_RE = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/;

/**
 * Anchored command-style triggers (connectApp.ts convention — never fire on
 * mid-sentence mentions of inviting). The registry strips a leading
 * "@Prest0n" before matching, so these work identically in gated group
 * threads.
 *
 * "add …" deliberately requires a member-ish noun: connect-app's pattern 3
 * matches "add <x> account/app/integration", and a bare "add jane to the
 * account" is ambiguous with thread-add — the panel affordance covers those.
 */
const INVITE_TRIGGER_PATTERNS: RegExp[] = [
  // "invite jane@acme.com", "please invite Jane to the team",
  // "can you invite a new member"
  /^(?:please\s+|can\s+you\s+|could\s+you\s+|i(?:'?d\s+like|\s+want|\s+need|\s+would\s+like)\s+to\s+)?invite\b([^\n]{0,160})/i,
  // "add a new member", "add a teammate to the workspace", "add someone to my team"
  /^(?:please\s+)?add\s+(?:a\s+|another\s+)?(?:new\s+)?(?:member|team\s*mate|team\s+member|colleague|user|someone|somebody)\b([^\n]{0,160})/i,
  // Spanish: "invita a maria@acme.com", "invitar a un nuevo miembro"
  /^(?:por\s+favor\s+)?invita(?:r|me)?\s+(?:a\s+)?([^\n]{0,160})/i,
];

export const matchInviteMemberTrigger = makeRegexTriggerWithExtractor(
  INVITE_TRIGGER_PATTERNS,
  (text) => ({
    inviteEmail: (text.match(EMAIL_RE) || [])[0],
    query: text,
  })
);

// ============================================================================
// State
// ============================================================================

export interface InviteMemberState {
  isRunning: boolean;
  lastEmail: string | null;
}

// ============================================================================
// Tool definition
// ============================================================================

export const inviteMemberGlobalTool: GlobalToolDefinition<InviteMemberState> = {
  id: INVITE_MEMBER_TOOL_ID,
  name: 'Invite Team Member',
  description:
    'Open the Team & Roles invite form right in the chat — name, email, member type, ' +
    'and granular role selection — prefilled with any email in the request. ' +
    'The invitation itself is permission-gated server-side (team.invite).',

  triggerPattern: matchInviteMemberTrigger,

  createInitialState: (): InviteMemberState => ({
    isRunning: false,
    lastEmail: null,
  }),

  buttonIds: {},

  steps: [
    {
      id: 'open-invite-form',
      name: 'Open Invite Form',
      activeDisplayName: 'opening the invite form',

      execute: async (
        ctx: StepContext<InviteMemberState>,
        rawInput?: unknown
      ): Promise<StepResult> => {
        const structured = (rawInput || {}) as { inviteEmail?: string; email?: string };
        const triggerText = ctx.triggerMessage?.text || '';
        const email =
          structured.inviteEmail ||
          structured.email ||
          (triggerText.match(EMAIL_RE) || [])[0] ||
          undefined;

        ctx.setState({ isRunning: true, lastEmail: email ?? null });
        const stream = ctx.messaging.showPending('opening the invite form');

        try {
          window.dispatchEvent(
            new CustomEvent(OPEN_INVITE_MEMBER_EVENT, { detail: { email } })
          );
          ctx.setState({ isRunning: false });
          stream.finalize({
            payload: {
              kind: 'team.invite_form_opened',
              toolId: INVITE_MEMBER_TOOL_ID,
              data: { prefilledEmail: email ?? null },
              summary: email
                ? `Opened the team-invite form prefilled with ${email}. The user picks a role (a recommended one is pre-selected) and clicks send; "invite another member" chains further invites.`
                : 'Opened the team-invite form (email, optional name, member type, role selection). The user fills it in and clicks send; "invite another member" chains further invites.',
            },
            sourceTag: 'team_invite_form_opened',
          });
          return { status: 'completed', output: { opened: true, email: email ?? null } };
        } catch (err) {
          ctx.setState({ isRunning: false });
          const msg = err instanceof Error ? err.message : String(err);
          stream.finalize({
            payload: {
              kind: 'team.invite_form_error',
              toolId: INVITE_MEMBER_TOOL_ID,
              severity: 'error',
              data: { errorMessage: msg },
              summary: `Opening the team-invite form failed: ${msg}. The user can still invite from Settings → Team & Roles.`,
            },
            sourceTag: 'team_invite_form_error',
          });
          return {
            status: 'failed',
            error: new Error(`invite-member open failed: ${msg}`),
            userFacingErrorShown: true,
          };
        }
      },

      cleanup: async (ctx: StepContext<InviteMemberState>) => {
        ctx.setState({ isRunning: false });
      },
    },
  ],

  isAuthorized: isAgenticWorkflowsAuthorized,

  theme: INVITE_MEMBER_THEME,

  canTriggerFromButton: false,
};

export default inviteMemberGlobalTool;
